import { useState } from "react";
import { 
  Mail, Loader2, Plus, RefreshCw, Trash2, Clock, Zap, 
  AlertTriangle, Server, CheckCircle, XCircle, Filter, Tag, Search, X, Key,
  Brain
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import SmartParsingSettings from "./SmartParsingSettings";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PROVIDERS = {
  "gmail.com": { server: "imap.gmail.com", port: 993, note: "Use App Password" },
  "outlook.com": { server: "outlook.office365.com", port: 993 },
  "yahoo.com": { server: "imap.mail.yahoo.com", port: 993, note: "Use App Password" },
};

function StatBox({ label, value, cls, color }) {
  const textColor = color ? `text-${color}-600` : "";
  return (
    <div className={`p-3 ${cls} rounded-lg text-center`}>
      <p className={`text-2xl font-bold ${textColor}`}>{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}

export default function EmailIntegration({ integrations, onRefresh }) {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);
  const [editKeyId, setEditKeyId] = useState(null);
  const [keyInput, setKeyInput] = useState("");
  const [reconnectId, setReconnectId] = useState(null);
  const [reconnectPassword, setReconnectPassword] = useState("");
  const [reconnecting, setReconnecting] = useState(false);
  
  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [imapServer, setImapServer] = useState("");
  const [imapPort, setImapPort] = useState(993);
  const [encryption, setEncryption] = useState("SSL");
  const [folder, setFolder] = useState("INBOX");

  const handleEmailChange = (val) => {
    setEmail(val);
    const domain = val.split("@")[1]?.toLowerCase();
    if (PROVIDERS[domain]) {
      setImapServer(PROVIDERS[domain].server);
      setImapPort(PROVIDERS[domain].port);
    } else if (domain) {
      setImapServer("mail." + domain);
      setImapPort(993);
    }
    setConnectionStatus(null);
  };

  const xhrRequest = (method, url, body, onSuccess, onError) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    xhr.withCredentials = true;
    xhr.onload = () => { try { onSuccess(JSON.parse(xhr.responseText), xhr.status); } catch (e) { onError("Parse error"); } };
    xhr.onerror = () => onError("Network error");
    xhr.send(body ? JSON.stringify(body) : null);
  };

  const handleTestConnection = () => {
    if (!email || !password || !imapServer) return toast.error("Fill all required fields");
    setTestingConnection(true);
    xhrRequest("POST", BACKEND_URL + "/api/email/test-connection", 
      { email_address: email, password, imap_server: imapServer, imap_port: imapPort, encryption, folder },
      (d) => { setTestingConnection(false); setConnectionStatus(d); d.status === "connected" ? toast.success("Connected!") : toast.error(d.message); },
      (e) => { setTestingConnection(false); toast.error(e); }
    );
  };

  const handleAdd = () => {
    if (!email || !password || !imapServer) return toast.error("Fill required fields");
    setFormLoading(true);
    xhrRequest("POST", BACKEND_URL + "/api/email/integrations",
      { email_address: email, provider_type: "imap", password, keywords: [], enabled: true, auto_sync_enabled: false,
        imap_settings: { server: imapServer, port: imapPort, encryption, folder, use_ssl: true } },
      (d, status) => {
        setFormLoading(false);
        if (status >= 200 && status < 300) { toast.success("Connected!"); resetForm(); setShowAddDialog(false); onRefresh(); }
        else toast.error(d.detail || "Failed", { duration: 10000 });
      },
      () => { setFormLoading(false); toast.error("Network error"); }
    );
  };

  const resetForm = () => { 
    setEmail(""); setPassword(""); setImapServer(""); setImapPort(993); 
    setEncryption("SSL"); setFolder("INBOX"); setConnectionStatus(null); 
  };

  const handleSync = (id) => {
    setSyncingId(id);
    xhrRequest("POST", BACKEND_URL + "/api/email/integrations/" + id + "/sync", null,
      (d, st) => { setSyncingId(null); if (st < 300) { setResult(d); setShowResult(true); onRefresh(); } else toast.error(d.detail || "Failed"); },
      () => { setSyncingId(null); toast.error("Network error"); }
    );
  };

  const startEditKey = (id, kws) => { setEditKeyId(id); setKeyInput((kws || []).join(", ")); };
  const cancelEditKey = () => { setEditKeyId(null); setKeyInput(""); };
  
  const saveKeywords = (id) => {
    const arr = keyInput.split(",").map(k => k.trim()).filter(k => k);
    fetch(BACKEND_URL + "/api/email/integrations/" + id, { method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ keywords: arr }) })
      .then(r => { if (r.ok) { toast.success("Saved"); cancelEditKey(); onRefresh(); } else toast.error("Failed"); });
  };

  const handleDelete = (id) => { 
    if (confirm("Delete?")) fetch(BACKEND_URL + "/api/email/integrations/" + id, { method: "DELETE", credentials: "include" })
      .then(() => { toast.success("Deleted"); onRefresh(); }); 
  };

  const handleToggle = (id, field, val) => {
    fetch(BACKEND_URL + "/api/email/integrations/" + id, { 
      method: "PUT", headers: { "Content-Type": "application/json" }, 
      credentials: "include", body: JSON.stringify({ [field]: val }) 
    }).then(onRefresh);
  };

  const handleReconnect = () => {
    if (!reconnectPassword) return toast.error("Enter your email password");
    setReconnecting(true);
    xhrRequest("POST", BACKEND_URL + "/api/email/integrations/" + reconnectId + "/reconnect",
      { password: reconnectPassword },
      (d, st) => {
        setReconnecting(false);
        if (st < 300) { toast.success("Reconnected! You can now sync."); setReconnectId(null); setReconnectPassword(""); onRefresh(); }
        else toast.error(d.detail || "Reconnect failed");
      },
      () => { setReconnecting(false); toast.error("Network error"); }
    );
  };

  const renderKeywordBadges = (kws) => {
    if (!kws || kws.length === 0) return <span className="text-sm text-slate-400 italic">No keywords - syncing all emails</span>;
    return <div className="flex flex-wrap gap-1">{kws.map((kw, i) => <Badge key={i} variant="secondary" className="bg-indigo-100 text-indigo-700"><Tag className="w-3 h-3 mr-1" />{kw}</Badge>)}</div>;
  };

  const providerNote = PROVIDERS[email.split("@")[1]?.toLowerCase()]?.note;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div className="flex gap-3 items-center">
              <Mail className="w-5 h-5 text-blue-600" />
              <div>
                <CardTitle>Email Integration (IMAP)</CardTitle>
                <CardDescription>Connect email accounts to capture leads from inbox</CardDescription>
              </div>
            </div>
            <Button onClick={() => setShowAddDialog(true)} data-testid="add-email-btn">
              <Plus className="w-4 h-4 mr-1" />Add Email
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {integrations.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Mail className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>No emails connected</p>
              <p className="text-sm">Add your first email integration to start capturing leads</p>
            </div>
          ) : integrations.map(i => (
            <div key={i.integration_id} className="p-4 mb-3 bg-slate-50 rounded-lg" data-testid={`integration-card-${i.integration_id}`}>
              <div className="flex justify-between mb-2">
                <div>
                  <p className="font-medium">{i.email_address}</p>
                  <p className="text-xs text-slate-500">{i.imap_server}:{i.imap_port}</p>
                </div>
                <Badge className={i.enabled ? "bg-green-100 text-green-700" : ""}>{i.enabled ? "Active" : "Off"}</Badge>
              </div>
              <div className="flex gap-4 mb-3 text-sm">
                <label className="flex items-center gap-2">Capture <Switch checked={i.enabled} onCheckedChange={v => handleToggle(i.integration_id, "enabled", v)} /></label>
                <label className="flex items-center gap-2"><Zap className="w-4 h-4" /> Auto <Switch checked={i.auto_sync_enabled} onCheckedChange={v => handleToggle(i.integration_id, "auto_sync_enabled", v)} /></label>
              </div>
              <div className="p-3 mb-3 bg-white rounded-lg border">
                <div className="flex items-center gap-2 mb-2 text-sm font-medium"><Filter className="w-4 h-4 text-indigo-500" />Lead Keywords</div>
                {editKeyId === i.integration_id ? (
                  <div className="flex gap-2">
                    <Input value={keyInput} onChange={e => setKeyInput(e.target.value)} placeholder="inquiry, quote (comma-separated)" className="flex-1" />
                    <Button size="sm" onClick={() => saveKeywords(i.integration_id)}><CheckCircle className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={cancelEditKey}><X className="w-4 h-4" /></Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="flex-1">{renderKeywordBadges(i.keywords)}</div>
                    <Button size="sm" variant="outline" onClick={() => startEditKey(i.integration_id, i.keywords)} data-testid={`edit-keywords-btn-${i.integration_id}`}><Search className="w-4 h-4 mr-1" />Edit</Button>
                  </div>
                )}
              </div>
              <div className="flex justify-between pt-2 border-t">
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleSync(i.integration_id)} disabled={syncingId === i.integration_id} className="bg-gradient-to-r from-indigo-500 to-purple-600" data-testid={`sync-btn-${i.integration_id}`}>
                    {syncingId === i.integration_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sync
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setReconnectId(i.integration_id); setReconnectPassword(""); }} data-testid={`reconnect-btn-${i.integration_id}`}>
                    <Key className="w-4 h-4 mr-1" /> Reconnect
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  {i.last_sync && <span className="text-xs text-slate-500"><Clock className="w-3 h-3 inline" /> {new Date(i.last_sync).toLocaleString()}</span>}
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(i.integration_id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Email Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={o => { if (!o) resetForm(); setShowAddDialog(o); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle><Mail className="w-5 h-5 inline mr-2 text-blue-600" />Connect Email</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Email *</Label><Input value={email} onChange={e => handleEmailChange(e.target.value)} placeholder="you@example.com" /></div>
            <div><Label>Password *</Label><Input type="password" value={password} onChange={e => setPassword(e.target.value)} />{providerNote && <p className="text-xs text-amber-600 mt-1"><AlertTriangle className="w-3 h-3 inline" /> {providerNote}</p>}</div>
            <div className="p-4 bg-slate-50 rounded-lg space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium"><Server className="w-4 h-4" /> IMAP Settings</div>
              <div><Label>Host *</Label><Input value={imapServer} onChange={e => setImapServer(e.target.value)} placeholder="mail.domain.com" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Port</Label><Input type="number" value={imapPort} onChange={e => setImapPort(parseInt(e.target.value) || 993)} /></div>
                <div><Label>Encryption</Label><Select value={encryption} onValueChange={setEncryption}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="SSL">SSL</SelectItem><SelectItem value="TLS">TLS</SelectItem><SelectItem value="None">None</SelectItem></SelectContent></Select></div>
              </div>
              <div><Label>Folder</Label><Input value={folder} onChange={e => setFolder(e.target.value)} placeholder="INBOX" /></div>
              <Button variant="outline" className="w-full" onClick={handleTestConnection} disabled={testingConnection || !email || !password || !imapServer}>{testingConnection ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Server className="w-4 h-4 mr-1" />}Test Connection</Button>
              {connectionStatus && <div className={`p-3 rounded-lg ${connectionStatus.status === "connected" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>{connectionStatus.status === "connected" ? <CheckCircle className="w-4 h-4 inline mr-1" /> : <XCircle className="w-4 h-4 inline mr-1" />}{connectionStatus.message}</div>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); setShowAddDialog(false); }}>Cancel</Button>
            <Button onClick={handleAdd} disabled={formLoading || !email || !password || !imapServer}>{formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}Connect</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Sync Result Dialog */}
      <Dialog open={showResult} onOpenChange={setShowResult}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-500" />Sync Complete</DialogTitle></DialogHeader>
          {result && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-3">
                <StatBox label="Total Scanned" value={result.total_emails_scanned || result.emails_processed || 0} cls="bg-slate-50" />
                <StatBox label="Matched Keywords" value={result.emails_matched || 0} cls="bg-indigo-50" color="indigo" />
                <StatBox label="Leads Created" value={result.leads_created || 0} cls="bg-green-50" color="green" />
                <StatBox label="Duplicates Skipped" value={result.duplicates_skipped || 0} cls="bg-amber-50" color="amber" />
              </div>
              {result.keyword_filter && result.keyword_filter.enabled && (
                <div className="p-3 bg-slate-100 rounded-lg">
                  <div className="flex items-center gap-2 text-sm font-medium mb-2"><Filter className="w-4 h-4" />Keywords Used</div>
                  {renderKeywordBadges(result.keyword_filter.keywords)}
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button onClick={() => setShowResult(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reconnect Dialog */}
      <Dialog open={!!reconnectId} onOpenChange={o => { if (!o) { setReconnectId(null); setReconnectPassword(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle><Key className="w-5 h-5 inline mr-2 text-amber-500" />Reconnect Email</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 mb-3">Re-enter your email password to fix the connection. This is needed when the stored password can no longer be decrypted.</p>
          <div className="space-y-3">
            <div>
              <Label>Email Password / App Password</Label>
              <Input type="password" value={reconnectPassword} onChange={e => setReconnectPassword(e.target.value)} placeholder="Enter your password" data-testid="reconnect-password-input" />
            </div>
            <p className="text-xs text-slate-400">For Gmail, use an App Password from your Google Account security settings.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReconnectId(null); setReconnectPassword(""); }}>Cancel</Button>
            <Button onClick={handleReconnect} disabled={reconnecting} data-testid="reconnect-confirm-btn" className="bg-amber-500 hover:bg-amber-400 text-white">
              {reconnecting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Reconnect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Smart Email Parsing Settings */}
      <Card className="mt-6" data-testid="smart-parsing-card">
        <CardHeader>
          <div className="flex gap-3 items-center">
            <Brain className="w-5 h-5 text-indigo-600" />
            <div>
              <CardTitle>Smart Email Parsing</CardTitle>
              <CardDescription>AI-powered email detection &amp; lead extraction settings</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <SmartParsingSettings />
        </CardContent>
      </Card>
    </>
  );
}
